// Hidden archive page - BBS posts from the novel rendered as terminal output
// Extended BBS interface with thread structure, flags, and corruption effects
import hidden from '../../content/hidden.json';
import { addCRTFlicker, addScanlines, glitchText } from '../../scripts/effects';
import { createReturnToHouse } from '../../scripts/navigation';

interface ArchiveThread {
  id: number;
  timestamp: string;
  author: string;
  subject: string;
  content: string;
  replies?: number;
  flags?: string[];
}

interface ArchiveData {
  title: string;
  subtitle: string;
  systemInfo: {
    baud: string;
    node: string;
    sysop: string;
    status: string;
  };
  threads: ArchiveThread[];
}

// Track intervals for cleanup
let archiveIntervals: number[] = [];

/**
 * Check if a thread has a specific flag
 */
function hasFlag(thread: ArchiveThread, flag: string): boolean {
  return thread.flags?.includes(flag) ?? false;
}

/**
 * Get CSS class modifiers based on thread flags
 */
function getThreadClasses(thread: ArchiveThread): string {
  const classes = ['archive-thread'];

  if (hasFlag(thread, 'DATA_CORRUPTION') || hasFlag(thread, 'UNRECOVERABLE')) {
    classes.push('archive-thread--corrupted');
  }
  if (hasFlag(thread, 'LOCKED')) {
    classes.push('archive-thread--locked');
  }
  if (hasFlag(thread, 'PINNED') || hasFlag(thread, 'SYSOP')) {
    classes.push('archive-thread--pinned');
  }
  if (hasFlag(thread, 'AUTOMATED')) {
    classes.push('archive-thread--system');
  }
  if (hasFlag(thread, 'TRUNCATED') || hasFlag(thread, 'INCOMPLETE')) {
    classes.push('archive-thread--truncated');
  }
  if (hasFlag(thread, 'TIMESTAMP_ERROR') || hasFlag(thread, 'AUTHOR_UNKNOWN')) {
    classes.push('archive-thread--anomaly');
  }
  if (hasFlag(thread, 'FINAL') || hasFlag(thread, 'FINAL_POST')) {
    classes.push('archive-thread--final');
  }
  if (hasFlag(thread, 'SYSTEM_INTERVENTION')) {
    classes.push('archive-thread--intervention');
  }

  return classes.join(' ');
}

/**
 * Render thread flags as terminal-style badges
 */
function renderFlags(flags?: string[]): string {
  if (!flags || flags.length === 0) return '';

  const flagsHtml = flags
    .map(flag => `<span class="archive-flag archive-flag--${flag.toLowerCase().replace(/_/g, '-')}">[${flag}]</span>`)
    .join(' ');

  return `<div class="archive-thread-flags">${flagsHtml}</div>`;
}

/**
 * Render a single thread
 */
function renderThread(thread: ArchiveThread): string {
  const threadClasses = getThreadClasses(thread);
  const isCorrupted = hasFlag(thread, 'DATA_CORRUPTION') || hasFlag(thread, 'UNRECOVERABLE');
  const isSystem = hasFlag(thread, 'AUTOMATED');

  return `
    <article class="${threadClasses}" data-thread-id="${thread.id}" role="listitem" aria-label="Thread ${thread.id}: ${thread.subject} by ${thread.author}">
      <header class="archive-thread-header">
        <div class="archive-thread-meta">
          <span class="archive-thread-id" aria-hidden="true">#${String(thread.id).padStart(4, '0')}</span>
          <span class="archive-thread-timestamp">${thread.timestamp}</span>
          ${thread.replies !== undefined ? `<span class="archive-thread-replies">[${thread.replies} ${thread.replies === 1 ? 'reply' : 'replies'}]</span>` : ''}
        </div>
        <div class="archive-thread-from">
          <span class="archive-thread-label">From:</span>
          <span class="archive-thread-author${isSystem ? ' archive-thread-author--system' : ''}">${thread.author}</span>
        </div>
        <div class="archive-thread-subject-line">
          <span class="archive-thread-label">Subj:</span>
          <span class="archive-thread-subject${isCorrupted ? ' archive-thread-subject--corrupted' : ''}">${thread.subject}</span>
        </div>
        ${renderFlags(thread.flags)}
      </header>
      <div class="archive-thread-content${isCorrupted ? ' archive-thread-content--corrupted' : ''}">
        <pre class="archive-thread-body">${thread.content}</pre>
      </div>
    </article>
  `;
}

/**
 * Render the hidden archive page
 */
export function renderArchive(): void {
  const app = document.getElementById('app');
  if (!app) return;

  app.innerHTML = '';

  const container = document.createElement('div');
  container.className = 'archive-container guestbook-scanlines';

  // Get archive data from hidden.json
  const archiveData = (hidden as any).archive as ArchiveData;
  const { title, subtitle, systemInfo, threads } = archiveData;

  // Render all threads
  const threadsHtml = threads
    .map(thread => renderThread(thread))
    .join('<div class="archive-divider" aria-hidden="true">────────────────────────────────────────────────────</div>');

  container.innerHTML = `
    <!--
      ═══════════════════════════════════════════════════════════════════════
      ARCHIVE ACCESS GRANTED - SEALED THREADS RECOVERED
      ═══════════════════════════════════════════════════════════════════════

      These BBS posts predate the novel's publication.
      Some users reference events that had not yet occurred.
      Timestamps show impossible dates, paradoxical sequences.

      The other hidden rooms:
        /basement - Where the AI narrator's voice echoes
        /attic    - Lost manuscript pages, authenticity disputed

      Discovery from the house:
        > "wesleyan" or "carlos" → basement
        > "chopra" → attic
        > Click empty spaces in the floor plan
        > Press Escape three times → attic

      All rooms discovered? Check: __chopras.status()

      "The manuscript is reading you back."
      ═══════════════════════════════════════════════════════════════════════
    -->
    <div class="archive-terminal guestbook-crt" role="main" aria-label="Archive - Hidden room containing sealed BBS threads">
      <header class="archive-header" id="main-content" tabindex="-1">
        <div class="archive-header-main">${title}</div>
        <div class="archive-header-sub">${subtitle}</div>
      </header>

      <div class="archive-system-info">
        <div class="archive-system-row">
          <span class="archive-system-label">BAUD:</span>
          <span class="archive-system-value">${systemInfo.baud}</span>
          <span class="archive-system-label">NODE:</span>
          <span class="archive-system-value">${systemInfo.node}</span>
          <span class="archive-system-label">SYSOP:</span>
          <span class="archive-system-value">${systemInfo.sysop}</span>
        </div>
        <div class="archive-system-status">
          <span class="archive-status-indicator"></span>
          <span>${systemInfo.status}</span>
        </div>
      </div>

      <div class="archive-divider archive-divider--double" aria-hidden="true">═══════════════════════════════════════════════════════</div>

      <div class="archive-command">> LIST ARCHIVED THREADS</div>
      <div class="archive-response">${threads.length} thread(s) recovered from sealed archive</div>

      <div class="archive-threads" role="list" aria-label="Archived BBS threads">
        ${threadsHtml}
      </div>

      <div class="archive-footer">
        <div class="archive-footer-status">
          <span class="archive-status-indicator archive-status-indicator--sealed"></span>
          <span>ARCHIVE SEALED - NO NEW POSTS ACCEPTED</span>
        </div>
        <div class="archive-footer-timestamp">Last modified: 03/17/24 23:59:59</div>
      </div>

      <nav class="archive-nav" aria-label="Page navigation"></nav>
    </div>
  `;

  app.appendChild(container);

  // Add CRT effects
  const terminal = container.querySelector('.archive-terminal');
  if (terminal) {
    addCRTFlicker(terminal as HTMLElement);
    addScanlines(terminal as HTMLElement);
  }

  // Add return to house navigation (green terminal style)
  const navContainer = container.querySelector('.archive-nav');
  if (navContainer) {
    const returnBtn = createReturnToHouse({ className: 'return-to-house--green' });
    navContainer.appendChild(returnBtn);
  }

  // Apply glitch effects to corrupted content
  const corruptedElements = container.querySelectorAll('.archive-thread-content--corrupted .archive-thread-body');
  corruptedElements.forEach((el, index) => {
    // Staggered glitch timing for each corrupted element
    setTimeout(() => {
      glitchText(el as HTMLElement, 800);
    }, index * 3000 + Math.random() * 2000);
  });

  // Apply periodic glitch to anomaly threads
  const anomalyThreads = container.querySelectorAll('.archive-thread--anomaly');
  anomalyThreads.forEach((el, index) => {
    const intervalId = window.setInterval(() => {
      const content = el.querySelector('.archive-thread-body') as HTMLElement;
      if (content) {
        glitchText(content, 400);
      }
    }, 8000 + index * 2000);
    archiveIntervals.push(intervalId);
  });
}

/**
 * Cleanup archive page - clear all tracked intervals
 */
export function cleanupArchive(): void {
  archiveIntervals.forEach((id) => {
    window.clearInterval(id);
  });
  archiveIntervals = [];
}
